/**
 * Price formatting for customer-facing billing copy (walkthrough F-35).
 *
 * The server sends prices in minor units (cents for USD). Customers read
 * "$39.00 / month", never "3900 minor units". This module only formats an
 * amount the server already returned; it never supplies an amount itself.
 *
 * The divisor comes from the currency's own fraction digits (via `Intl`), so a
 * zero-decimal currency such as JPY is not divided by 100.
 */

const PERIOD_LABELS: Record<string, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  monthly: 'month',
  year: 'year',
  yearly: 'year',
  annual: 'year',
};

function currencyFormatter(currency: string): Intl.NumberFormat | null {
  if (!/^[A-Za-z]{3}$/.test(currency)) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() });
  } catch {
    return null;
  }
}

/**
 * Format a minor-unit amount as money, e.g. `formatMoney(3900, 'USD')` →
 * `"$39.00"`. Returns `null` when the amount or currency is missing or
 * unusable, so the caller can show an honest fallback instead of a guess.
 */
export function formatMoney(amountMinor: number | null | undefined, currency: string | null | undefined): string | null {
  if (typeof amountMinor !== 'number' || !Number.isInteger(amountMinor) || amountMinor < 0) return null;
  if (typeof currency !== 'string') return null;
  const formatter = currencyFormatter(currency);
  if (!formatter) return null;
  const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(amountMinor / 10 ** digits);
}

/** "month" → "month"; an unknown period is shown as sent rather than dropped. */
export function periodLabel(period: string | null | undefined): string | null {
  if (typeof period !== 'string' || !period.trim()) return null;
  return PERIOD_LABELS[period.trim().toLowerCase()] ?? period.trim();
}

/**
 * Format a recurring price, e.g. `formatPrice(3900, 'USD', 'month')` →
 * `"$39.00 / month"`. Without a period, only the amount is shown.
 */
export function formatPrice(
  amountMinor: number | null | undefined,
  currency: string | null | undefined,
  period?: string | null,
): string | null {
  const money = formatMoney(amountMinor, currency);
  if (money === null) return null;
  const label = periodLabel(period);
  return label ? `${money} / ${label}` : money;
}
